import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Typography, Chip, Button, CircularProgress,
  TextField, Select, MenuItem, FormControl, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Collapse, Divider,
  Tooltip, Menu,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useRef } from 'react';
import { getAto, updateAto, getControls, getFamilySummary, updateControl, exportControls, exportTestResults, uploadAtoLetter } from '../api/ato';

// ── Constants ──────────────────────────────────────────────────────────────────

const STATUS_COLOR = {
  ACTIVE:  '#4caf50', EXPIRED: '#f44336', PENDING: '#ff9800',
  IATT:    '#00bcd4', IATO:    '#ff9800',
};

const COMPLIANCE_COLOR = {
  'Compliant':     '#4caf50',
  'Non-Compliant': '#f44336',
  'Not Applicable':'#607d8b',
  'Not Reviewed':  '#7a8fa6',
};

const FAMILIES = [
  { code: 'AC', name: 'Access Control' },
  { code: 'AU', name: 'Audit & Accountability' },
  { code: 'CA', name: 'Assessment & Authorization' },
  { code: 'CM', name: 'Configuration Management' },
  { code: 'IA', name: 'Identification & Auth' },
  { code: 'IR', name: 'Incident Response' },
  { code: 'MA', name: 'Maintenance' },
  { code: 'MP', name: 'Media Protection' },
  { code: 'PE', name: 'Physical & Environmental' },
  { code: 'PL', name: 'Planning' },
  { code: 'PM', name: 'Program Management' },
  { code: 'RA', name: 'Risk Assessment' },
  { code: 'SA', name: 'System & Services Acq.' },
  { code: 'SC', name: 'Comms Protection' },
  { code: 'SI', name: 'System & Info Integrity' },
  { code: 'SR', name: 'Supply Chain Risk Mgmt' },
];

const FREQ_OPTIONS    = ['Annually', 'Semi-Annually', 'Quarterly', 'Monthly', 'Continuous'];
const STATUS_OPTIONS  = ['Compliant', 'Non-Compliant', 'Not Applicable', 'Not Reviewed'];
const CIA_LEVELS      = ['High', 'Moderate', 'Low'];
const CIA_LEVEL_COLOR = { High: '#f44336', Moderate: '#ff9800', Low: '#4caf50' };
const METHOD_OPTIONS  = ['Examine', 'Interview', 'Test', 'Examine/Interview', 'Examine/Test', 'Interview/Test', 'Examine/Interview/Test'];
const ATO_STATUSES    = ['ACTIVE', 'PENDING', 'EXPIRED', 'IATT', 'IATO'];
const ATO_TYPES       = ['ATO', 'IATT', 'IATO'];
const CLASSIFICATIONS = ['UNCLASSIFIED', 'CUI', 'SECRET', 'TOP SECRET'];


// ── ATO Record Banner ──────────────────────────────────────────────────────────

function AtoBanner({ ato, systemId, onUpdated }) {
  const [editing, setEditing]         = useState(false);
  const [form, setForm]               = useState({});
  const [saving, setSaving]           = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [parsedLetter, setParsedLetter] = useState(null);
  const fileInputRef = useRef(null);

  const openEdit = () => { setForm({ ...ato }); setEditing(true); };

  const save = async () => {
    setSaving(true);
    await updateAto(systemId, form);
    setSaving(false);
    setEditing(false);
    onUpdated();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadError('');
    try {
      const parsed = await uploadAtoLetter(systemId, file);
      setParsedLetter({ ...ato, ...Object.fromEntries(Object.entries(parsed).filter(([, v]) => v != null)) });
    } catch (err) {
      setUploadError(err?.detail || 'Failed to parse PDF. Make sure it is a text-based (not scanned) PDF.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const confirmParsed = async () => {
    setSaving(true);
    await updateAto(systemId, parsedLetter);
    setSaving(false);
    setParsedLetter(null);
    onUpdated();
  };

  const days     = ato?.days_remaining;
  const daysColor = days == null ? '#7a8fa6' : days < 0 ? '#f44336' : days < 30 ? '#ff9800' : days < 90 ? '#ffeb3b' : '#4caf50';
  const daysLabel = days == null ? 'No expiration set' : days < 0 ? `Expired ${Math.abs(days)} days ago` : `${days} days remaining`;

  return (
    <>
      <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>

          {/* Left — system info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: 18 }}>
                {ato?.system_name || 'System Name'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                {ato?.cage_code && <Typography sx={{ color: '#7a8fa6', fontSize: 12, fontFamily: 'monospace' }}>{ato.cage_code}</Typography>}
                {ato?.classification && (
                  <Chip label={ato.classification} size="small" sx={{ fontSize: 10, height: 18, backgroundColor: '#1e3a5f', color: '#00bcd4', fontWeight: 700 }} />
                )}
              </Box>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ borderColor: '#1e3a5f' }} />

            <Chip
              label={ato?.status || 'PENDING'}
              sx={{ backgroundColor: `${STATUS_COLOR[ato?.status] || '#ff9800'}22`, color: STATUS_COLOR[ato?.status] || '#ff9800', fontWeight: 700, fontSize: 13 }}
            />
            <Chip
              label={ato?.accreditation_type || 'ATO'}
              size="small"
              sx={{ backgroundColor: '#0d2137', color: '#00bcd4', fontWeight: 700, fontSize: 11 }}
            />
          </Box>

          {/* Center — dates */}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Authorized</Typography>
              <Typography sx={{ color: '#c0cfe0', fontSize: 13, fontWeight: 600 }}>{ato?.authorization_date || '—'}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Expires</Typography>
              <Typography sx={{ color: '#c0cfe0', fontSize: 13, fontWeight: 600 }}>{ato?.expiration_date || '—'}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Countdown</Typography>
              <Typography sx={{ color: daysColor, fontSize: 13, fontWeight: 700 }}>{daysLabel}</Typography>
            </Box>
          </Box>

          {/* Right — personnel */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {[['AO', ato?.authorizing_official], ['ISSM', ato?.issm_name], ['ISSO', ato?.isso_name]].map(([role, name]) => (
              <Box key={role} sx={{ textAlign: 'center' }}>
                <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{role}</Typography>
                <Typography sx={{ color: name ? '#c0cfe0' : '#4a5a70', fontSize: 12 }}>{name || 'Not set'}</Typography>
              </Box>
            ))}
            <input ref={fileInputRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleFileChange} />
            <Tooltip title="Upload Authorization Letter (PDF)">
              <span>
                <IconButton onClick={() => { setUploadError(''); fileInputRef.current?.click(); }} disabled={uploading}
                  sx={{ color: '#7a8fa6', '&:hover': { color: '#4caf50' } }}>
                  {uploading ? <CircularProgress size={16} sx={{ color: '#00bcd4' }} /> : <UploadFileIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Edit ATO Record">
              <IconButton onClick={openEdit} sx={{ color: '#7a8fa6', '&:hover': { color: '#00bcd4' } }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editing} onClose={() => setEditing(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } }}>
        <DialogTitle sx={{ color: '#ffffff', borderBottom: '1px solid #1e3a5f' }}>Edit ATO Record</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            {[
              ['system_name', 'System Name'], ['cage_code', 'CAGE Code'],
              ['authorizing_official', 'Authorizing Official (AO)'],
              ['issm_name', 'ISSM Name'], ['isso_name', 'ISSO Name'],
              ['authorization_date', 'Authorization Date'], ['expiration_date', 'Expiration Date'],
            ].map(([key, label]) => (
              <TextField key={key} label={label} value={form[key] || ''} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                size="small" type={key.includes('date') ? 'date' : 'text'} InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputBase-root': { backgroundColor: '#0d1825', color: '#c0cfe0' }, '& label': { color: '#7a8fa6' }, '& fieldset': { borderColor: '#1e3a5f' } }} />
            ))}
            {[
              ['status', 'Status', ATO_STATUSES],
              ['accreditation_type', 'Type', ATO_TYPES],
              ['classification', 'Classification', CLASSIFICATIONS],
            ].map(([key, label, opts]) => (
              <FormControl key={key} size="small">
                <Typography sx={{ color: '#7a8fa6', fontSize: 11, mb: 0.5 }}>{label}</Typography>
                <Select value={form[key] || ''} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                  sx={{ backgroundColor: '#0d1825', color: '#c0cfe0', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }}>
                  {opts.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            ))}
            <TextField label="Notes" value={form.notes || ''} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              multiline rows={3} size="small" sx={{ gridColumn: '1 / -1', '& .MuiInputBase-root': { backgroundColor: '#0d1825', color: '#c0cfe0' }, '& label': { color: '#7a8fa6' }, '& fieldset': { borderColor: '#1e3a5f' } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #1e3a5f', p: 2 }}>
          <Button onClick={() => setEditing(false)} sx={{ color: '#7a8fa6' }}>Cancel</Button>
          <Button onClick={save} disabled={saving} variant="contained"
            sx={{ backgroundColor: '#00bcd4', color: '#000', fontWeight: 700, '&:hover': { backgroundColor: '#00acc1' } }}>
            {saving ? <CircularProgress size={16} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload error */}
      {uploadError && (
        <Box sx={{ mt: 1, p: 1.5, backgroundColor: '#2a0a0a', border: '1px solid #f44336', borderRadius: 1 }}>
          <Typography sx={{ color: '#f44336', fontSize: 12 }}>{uploadError}</Typography>
        </Box>
      )}

      {/* Parsed letter confirmation dialog */}
      <Dialog open={!!parsedLetter} onClose={() => setParsedLetter(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } }}>
        <DialogTitle sx={{ color: '#ffffff', borderBottom: '1px solid #1e3a5f' }}>
          Confirm Parsed Authorization Letter
          <Typography sx={{ color: '#7a8fa6', fontSize: 12, fontWeight: 400, mt: 0.5 }}>
            Review and correct any fields before saving.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            {[
              ['system_name', 'System Name'], ['cage_code', 'CAGE Code'],
              ['authorizing_official', 'Authorizing Official (AO)'],
              ['issm_name', 'ISSM Name'], ['isso_name', 'ISSO Name'],
              ['authorization_date', 'Authorization Date'], ['expiration_date', 'Expiration Date'],
            ].map(([key, label]) => (
              <TextField key={key} label={label} value={parsedLetter?.[key] || ''} onChange={e => setParsedLetter(p => ({ ...p, [key]: e.target.value }))}
                size="small" type={key.includes('date') ? 'date' : 'text'} InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputBase-root': { backgroundColor: '#0d1825', color: '#c0cfe0' }, '& label': { color: '#7a8fa6' }, '& fieldset': { borderColor: '#1e3a5f' } }} />
            ))}
            {[
              ['status', 'Status', ATO_STATUSES],
              ['accreditation_type', 'Type', ATO_TYPES],
              ['classification', 'Classification', CLASSIFICATIONS],
            ].map(([key, label, opts]) => (
              <FormControl key={key} size="small">
                <Typography sx={{ color: '#7a8fa6', fontSize: 11, mb: 0.5 }}>{label}</Typography>
                <Select value={parsedLetter?.[key] || ''} onChange={e => setParsedLetter(p => ({ ...p, [key]: e.target.value }))}
                  sx={{ backgroundColor: '#0d1825', color: '#c0cfe0', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }}>
                  {opts.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            ))}
            <TextField label="Notes" value={parsedLetter?.notes || ''} onChange={e => setParsedLetter(p => ({ ...p, notes: e.target.value }))}
              multiline rows={3} size="small" sx={{ gridColumn: '1 / -1', '& .MuiInputBase-root': { backgroundColor: '#0d1825', color: '#c0cfe0' }, '& label': { color: '#7a8fa6' }, '& fieldset': { borderColor: '#1e3a5f' } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #1e3a5f', p: 2 }}>
          <Button onClick={() => setParsedLetter(null)} sx={{ color: '#7a8fa6' }}>Cancel</Button>
          <Button onClick={confirmParsed} disabled={saving} variant="contained"
            sx={{ backgroundColor: '#4caf50', color: '#000', fontWeight: 700 }}>
            {saving ? <CircularProgress size={16} /> : 'Save ATO Record'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}


// ── Control Row ────────────────────────────────────────────────────────────────

function ControlRow({ ctrl, systemId, onUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const [editing,  setEditing]  = useState(false);
  const [form,     setForm]     = useState({});
  const [saving,   setSaving]   = useState(false);

  const openEdit = (e) => { e.stopPropagation(); setForm({ ...ctrl }); setEditing(true); };

  const save = async () => {
    setSaving(true);
    await updateControl(systemId, ctrl.id, form);
    setSaving(false);
    setEditing(false);
    onUpdated();
  };

  const color = COMPLIANCE_COLOR[ctrl.compliance_status] || '#7a8fa6';

  return (
    <>
      <Paper sx={{
        backgroundColor: '#111827', border: '1px solid #1e3a5f',
        borderLeft: `3px solid ${color}`, borderRadius: 1,
        '&:hover': { backgroundColor: '#0d1f33' },
      }}>
        <Box onClick={() => setExpanded(e => !e)} sx={{
          display: 'grid', gridTemplateColumns: '90px 1fr 140px 130px 120px 100px 32px',
          alignItems: 'center', padding: '10px 14px', gap: 1, cursor: 'pointer',
        }}>
          <Typography sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>{ctrl.acronym}</Typography>
          <Typography sx={{ color: '#c0cfe0', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ctrl.title}</Typography>
          <Chip label={ctrl.compliance_status || 'Not Reviewed'} size="small" sx={{
            backgroundColor: `${color}22`, color, fontWeight: 700, fontSize: 10, height: 20, width: 'fit-content',
          }} />
          <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>{ctrl.frequency || '—'}</Typography>
          <Typography sx={{ color: '#4a5a70', fontFamily: 'monospace', fontSize: 11 }}>{ctrl.last_assessed || '—'}</Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {ctrl.is_common && <Chip label="Inherited" size="small" sx={{ fontSize: 9, height: 16, backgroundColor: '#0d2137', color: '#00bcd4' }} />}
            {ctrl.slcm && <Chip label="SLCM" size="small" sx={{ fontSize: 9, height: 16, backgroundColor: '#1a2a0a', color: '#4caf50' }} />}
          </Box>
          <Box sx={{ color: '#4a5a70', display: 'flex', alignItems: 'center' }}>
            {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Divider sx={{ borderColor: '#1e3a5f' }} />
          <Box sx={{ p: 2, backgroundColor: '#0a0e1a', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {ctrl.control_text && (
              <Box>
                <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Control Text</Typography>
                <Typography sx={{ color: '#c0cfe0', fontSize: 12, whiteSpace: 'pre-wrap' }}>{ctrl.control_text}</Typography>
              </Box>
            )}
            {ctrl.narrative && (
              <Box>
                <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Implementation Narrative</Typography>
                <Typography sx={{ color: '#c0cfe0', fontSize: 12, whiteSpace: 'pre-wrap' }}>{ctrl.narrative}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {ctrl.test_method && <Box><Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Test Method</Typography><Typography sx={{ color: '#c0cfe0', fontSize: 12 }}>{ctrl.test_method}</Typography></Box>}
              {ctrl.responsible_entities && <Box><Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Responsible</Typography><Typography sx={{ color: '#c0cfe0', fontSize: 12 }}>{ctrl.responsible_entities}</Typography></Box>}
              {ctrl.criticality && <Box><Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Criticality</Typography><Typography sx={{ color: '#c0cfe0', fontSize: 12 }}>{ctrl.criticality}</Typography></Box>}
              {ctrl.provider && <Box><Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Provider</Typography><Typography sx={{ color: '#c0cfe0', fontSize: 12 }}>{ctrl.provider}</Typography></Box>}
            </Box>
            {(ctrl.confidentiality || ctrl.integrity || ctrl.availability) && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>CIA Impact:</Typography>
                {[['C', ctrl.confidentiality], ['I', ctrl.integrity], ['A', ctrl.availability]].map(([lbl, val]) => val && (
                  <Chip key={lbl} label={`${lbl}: ${val}`} size="small" sx={{
                    fontSize: 10, height: 18,
                    backgroundColor: `${CIA_LEVEL_COLOR[val] || '#607d8b'}22`,
                    color: CIA_LEVEL_COLOR[val] || '#607d8b',
                    fontWeight: 700,
                  }} />
                ))}
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="small" startIcon={<EditIcon sx={{ fontSize: 13 }} />} onClick={openEdit}
                sx={{ color: '#00bcd4', borderColor: '#00bcd4', fontSize: 11, border: '1px solid', '&:hover': { backgroundColor: '#00bcd411' } }}>
                Edit Control
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editing} onClose={() => setEditing(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } }}>
        <DialogTitle sx={{ color: '#ffffff', borderBottom: '1px solid #1e3a5f', fontSize: 15 }}>
          {ctrl.acronym} — {ctrl.title}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            {[
              ['compliance_status', 'Compliance Status', STATUS_OPTIONS],
              ['test_method', 'Test Method', METHOD_OPTIONS],
              ['frequency', 'Review Frequency', FREQ_OPTIONS],
              ['criticality', 'Criticality', ['High', 'Medium', 'Low']],
              ['impact', 'Impact', ['High', 'Medium', 'Low']],
              ['risk_assessment', 'Risk Assessment', ['High', 'Medium', 'Low', 'Very Low']],
            ].map(([key, label, opts]) => (
              <FormControl key={key} size="small">
                <Typography sx={{ color: '#7a8fa6', fontSize: 11, mb: 0.5 }}>{label}</Typography>
                <Select value={form[key] || ''} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                  sx={{ backgroundColor: '#0d1825', color: '#c0cfe0', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }}>
                  {opts.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            ))}
            <TextField label="Last Assessed Date" type="date" value={form.last_assessed || ''} onChange={e => setForm(p => ({ ...p, last_assessed: e.target.value }))}
              size="small" InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiInputBase-root': { backgroundColor: '#0d1825', color: '#c0cfe0' }, '& label': { color: '#7a8fa6' }, '& fieldset': { borderColor: '#1e3a5f' } }} />
            <TextField label="Responsible Entities" value={form.responsible_entities || ''} onChange={e => setForm(p => ({ ...p, responsible_entities: e.target.value }))}
              size="small"
              sx={{ '& .MuiInputBase-root': { backgroundColor: '#0d1825', color: '#c0cfe0' }, '& label': { color: '#7a8fa6' }, '& fieldset': { borderColor: '#1e3a5f' } }} />
            <TextField label="Provider (if inherited)" value={form.provider || ''} onChange={e => setForm(p => ({ ...p, provider: e.target.value }))}
              size="small"
              sx={{ '& .MuiInputBase-root': { backgroundColor: '#0d1825', color: '#c0cfe0' }, '& label': { color: '#7a8fa6' }, '& fieldset': { borderColor: '#1e3a5f' } }} />
            <TextField label="Implementation Narrative" value={form.narrative || ''} onChange={e => setForm(p => ({ ...p, narrative: e.target.value }))}
              multiline rows={4} size="small" sx={{ gridColumn: '1 / -1', '& .MuiInputBase-root': { backgroundColor: '#0d1825', color: '#c0cfe0' }, '& label': { color: '#7a8fa6' }, '& fieldset': { borderColor: '#1e3a5f' } }} />
            <TextField label="Threat" value={form.threat || ''} onChange={e => setForm(p => ({ ...p, threat: e.target.value }))}
              multiline rows={2} size="small" sx={{ '& .MuiInputBase-root': { backgroundColor: '#0d1825', color: '#c0cfe0' }, '& label': { color: '#7a8fa6' }, '& fieldset': { borderColor: '#1e3a5f' } }} />
            <TextField label="Vulnerability" value={form.vulnerability || ''} onChange={e => setForm(p => ({ ...p, vulnerability: e.target.value }))}
              multiline rows={2} size="small" sx={{ '& .MuiInputBase-root': { backgroundColor: '#0d1825', color: '#c0cfe0' }, '& label': { color: '#7a8fa6' }, '& fieldset': { borderColor: '#1e3a5f' } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #1e3a5f', p: 2 }}>
          <Button onClick={() => setEditing(false)} sx={{ color: '#7a8fa6' }}>Cancel</Button>
          <Button onClick={save} disabled={saving} variant="contained"
            sx={{ backgroundColor: '#00bcd4', color: '#000', fontWeight: 700, '&:hover': { backgroundColor: '#00acc1' } }}>
            {saving ? <CircularProgress size={16} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}


// ── Main Page ──────────────────────────────────────────────────────────────────

export default function AtoRmf({ systemId }) {
  const [ato,           setAto]           = useState(null);
  const [controls,      setControls]      = useState([]);
  const [families,      setFamilies]      = useState([]);
  const [selectedFamily,setSelectedFamily] = useState('ALL');
  const [filterStatus,  setFilterStatus]  = useState([]);
  const [filterFreq,    setFilterFreq]    = useState([]);
  const [filterC,       setFilterC]       = useState('');
  const [filterI,       setFilterI]       = useState('');
  const [filterA,       setFilterA]       = useState('');
  const [filterApplied, setFilterApplied] = useState(true);
  const [loading,       setLoading]       = useState(true);
  const [exportAnchor,  setExportAnchor]  = useState(null);

  const load = useCallback(() => {
    if (!systemId) return;
    Promise.all([getAto(systemId), getControls(systemId, {}), getFamilySummary(systemId)])
      .then(([atoData, ctrlData, famData]) => {
        setAto(atoData);
        setControls(ctrlData);
        setFamilies(famData);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [systemId]);

  useEffect(() => { load(); }, [load]);

  const familyMap = useMemo(() => {
    const m = {};
    families.forEach(f => { m[f.family] = f; });
    return m;
  }, [families]);

  const summary = useMemo(() => ({
    total:       controls.length,
    compliant:   controls.filter(c => c.compliance_status === 'Compliant').length,
    nonCompliant:controls.filter(c => c.compliance_status === 'Non-Compliant').length,
    notReviewed: controls.filter(c => !c.compliance_status || c.compliance_status === 'Not Reviewed').length,
  }), [controls]);

  const filtered = useMemo(() => controls.filter(c => {
    if (filterApplied !== null && c.applied !== filterApplied) return false;
    if (selectedFamily !== 'ALL' && c.family !== selectedFamily) return false;
    if (filterStatus.length > 0 && !filterStatus.includes(c.compliance_status || 'Not Reviewed')) return false;
    if (filterFreq.length > 0 && !filterFreq.includes(c.frequency)) return false;
    if (filterC && c.confidentiality !== filterC) return false;
    if (filterI && c.integrity !== filterI) return false;
    if (filterA && c.availability !== filterA) return false;
    return true;
  }), [controls, selectedFamily, filterStatus, filterFreq, filterC, filterI, filterA, filterApplied]);

  if (loading) return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <CircularProgress sx={{ color: '#00bcd4' }} />
    </Box>
  );

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3 }}>

      {/* ATO Record Banner */}
      <AtoBanner ato={ato} systemId={systemId} onUpdated={load} />

      {/* Summary Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {[
          { label: 'Total Controls', value: summary.total,        color: '#ffffff',  status: null },
          { label: 'Compliant',      value: summary.compliant,    color: '#4caf50',  status: 'Compliant' },
          { label: 'Non-Compliant',  value: summary.nonCompliant, color: '#f44336',  status: 'Non-Compliant' },
          { label: 'Not Reviewed',   value: summary.notReviewed,  color: '#7a8fa6',  status: 'Not Reviewed' },
        ].map(s => {
          const active = s.status && filterStatus.includes(s.status);
          const toggle = () => {
            if (!s.status) { setFilterStatus([]); return; }
            setFilterStatus(prev => prev.includes(s.status) ? prev.filter(x => x !== s.status) : [...prev, s.status]);
          };
          return (
            <Paper key={s.label} onClick={toggle} sx={{
              flex: 1, backgroundColor: active ? `${s.color}11` : '#111827',
              border: `1px solid ${active ? s.color : '#1e3a5f'}`,
              borderRadius: 2, p: '16px 20px', textAlign: 'center',
              cursor: 'pointer', transition: 'all 0.15s',
              '&:hover': { borderColor: s.color, backgroundColor: `${s.color}0d` },
            }}>
              <Typography sx={{ color: s.color, fontSize: 36, fontWeight: 700, lineHeight: 1 }}>{s.value}</Typography>
              <Typography sx={{ color: active ? s.color : '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.5 }}>{s.label}</Typography>
            </Paper>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>

        {/* Family Sidebar */}
        <Paper sx={{ width: 240, flexShrink: 0, backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: '10px 14px', borderBottom: '1px solid #1e3a5f', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Control Families</Typography>
          </Box>

          {/* All */}
          <Box onClick={() => setSelectedFamily('ALL')} sx={{
            p: '10px 14px', cursor: 'pointer',
            backgroundColor: selectedFamily === 'ALL' ? '#0d2137' : 'transparent',
            borderLeft: `3px solid ${selectedFamily === 'ALL' ? '#00bcd4' : 'transparent'}`,
            '&:hover': { backgroundColor: '#0d1f33' },
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ color: selectedFamily === 'ALL' ? '#ffffff' : '#c0cfe0', fontSize: 12, fontWeight: 700 }}>All Families</Typography>
              <Typography sx={{ color: '#7a8fa6', fontSize: 11 }}>{controls.length}</Typography>
            </Box>
          </Box>

          {FAMILIES.map(f => {
            const fam     = familyMap[f.code] || { compliant: 0, total: 0 };
            const total   = fam.total || 0;
            const comp    = fam.compliant || 0;
            const pct     = total > 0 ? Math.round((comp / total) * 100) : 0;
            const color   = pct >= 80 ? '#4caf50' : pct >= 50 ? '#ff9800' : total > 0 ? '#f44336' : '#4a5a70';
            const isActive = selectedFamily === f.code;
            return (
              <Box key={f.code} onClick={() => setSelectedFamily(f.code)} sx={{
                p: '10px 14px', cursor: 'pointer',
                backgroundColor: isActive ? '#0d2137' : 'transparent',
                borderLeft: `3px solid ${isActive ? '#00bcd4' : 'transparent'}`,
                '&:hover': { backgroundColor: '#0d1f33' },
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Box>
                    <Typography component="span" sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 11, fontWeight: 700, mr: 0.75 }}>{f.code}</Typography>
                    <Typography component="span" sx={{ color: isActive ? '#ffffff' : '#c0cfe0', fontSize: 11 }}>{f.name}</Typography>
                  </Box>
                  <Typography sx={{ color, fontSize: 11, fontWeight: 700 }}>{total > 0 ? `${comp}/${total}` : '—'}</Typography>
                </Box>
                {total > 0 && (
                  <Box sx={{ height: 3, borderRadius: 1, backgroundColor: '#1e3a5f' }}>
                    <Box sx={{ height: 3, borderRadius: 1, backgroundColor: color, width: `${pct}%` }} />
                  </Box>
                )}
              </Box>
            );
          })}
        </Paper>

        {/* Controls Table */}
        <Box sx={{ flex: 1, minWidth: 0 }}>

          {/* Toolbar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: 15, flex: 1 }}>
              {selectedFamily === 'ALL' ? 'All Controls' : `${selectedFamily} — ${FAMILIES.find(f => f.code === selectedFamily)?.name}`}
              <Typography component="span" sx={{ color: '#4a5a70', fontSize: 12, ml: 1 }}>({filtered.length})</Typography>
            </Typography>

            {controls.length === 0 && (
              <Typography sx={{ color: '#4a5a70', fontSize: 12 }}>
                No controls loaded. Add controls or use the seed API.
              </Typography>
            )}

            <Button size="small" variant="outlined" onClick={(e) => setExportAnchor(e.currentTarget)}
              startIcon={<DownloadIcon sx={{ fontSize: 13 }} />} endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 13 }} />}
              sx={{ borderColor: '#1e3a5f', color: '#7a8fa6', fontSize: 11, '&:hover': { borderColor: '#00bcd4', color: '#00bcd4' } }}>
              Export
            </Button>
            <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}
              PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } }}>
              <MenuItem onClick={() => { exportControls(systemId); setExportAnchor(null); }}
                sx={{ color: '#c0cfe0', fontSize: 13, '&:hover': { backgroundColor: '#0d2137' } }}>
                ControlInfoExport (eMASS)
              </MenuItem>
              <MenuItem onClick={() => { exportTestResults(systemId); setExportAnchor(null); }}
                sx={{ color: '#c0cfe0', fontSize: 13, '&:hover': { backgroundColor: '#0d2137' } }}>
                Test Results Export (eMASS)
              </MenuItem>
            </Menu>
          </Box>

          {/* Status + Applied Filters */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography sx={{ color: '#7a8fa6', fontSize: 11, fontWeight: 700 }}>Status:</Typography>
            {STATUS_OPTIONS.map(s => {
              const active = filterStatus.includes(s);
              const color  = COMPLIANCE_COLOR[s] || '#7a8fa6';
              return (
                <Chip key={s} label={s} size="small" onClick={() =>
                  setFilterStatus(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])
                } sx={{
                  fontSize: 10, height: 20, cursor: 'pointer',
                  backgroundColor: active ? `${color}33` : '#0d1825',
                  color: active ? color : '#4a5a70',
                  border: `1px solid ${active ? color : '#1e3a5f'}`,
                  fontWeight: active ? 700 : 400,
                  '&:hover': { backgroundColor: `${color}22`, color },
                }} />
              );
            })}

            <Box sx={{ width: '1px', height: 16, backgroundColor: '#1e3a5f', mx: 0.5 }} />

            <Typography sx={{ color: '#7a8fa6', fontSize: 11, fontWeight: 700 }}>Applicable:</Typography>
            {[
              { label: 'Applied',     val: true  },
              { label: 'Not Applied', val: false },
              { label: 'All',         val: null  },
            ].map(({ label, val }) => {
              const active = filterApplied === val;
              return (
                <Chip key={label} label={label} size="small" onClick={() => setFilterApplied(val)}
                  sx={{
                    fontSize: 10, height: 20, cursor: 'pointer',
                    backgroundColor: active ? '#00bcd433' : '#0d1825',
                    color: active ? '#00bcd4' : '#4a5a70',
                    border: `1px solid ${active ? '#00bcd4' : '#1e3a5f'}`,
                    fontWeight: active ? 700 : 400,
                    '&:hover': { backgroundColor: '#00bcd422', color: '#00bcd4' },
                  }} />
              );
            })}
          </Box>

          {/* CIA Impact Filters */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { label: 'C', title: 'Confidentiality', val: filterC, set: setFilterC },
              { label: 'I', title: 'Integrity',       val: filterI, set: setFilterI },
              { label: 'A', title: 'Availability',    val: filterA, set: setFilterA },
            ].map(({ label, title, val, set }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ color: '#7a8fa6', fontSize: 11, fontWeight: 700, mr: 0.25 }}>{title}:</Typography>
                {CIA_LEVELS.map(lvl => (
                  <Chip
                    key={lvl}
                    label={lvl}
                    size="small"
                    onClick={() => set(v => v === lvl ? '' : lvl)}
                    sx={{
                      fontSize: 10, height: 20, cursor: 'pointer',
                      backgroundColor: val === lvl ? `${CIA_LEVEL_COLOR[lvl]}33` : '#0d1825',
                      color: val === lvl ? CIA_LEVEL_COLOR[lvl] : '#4a5a70',
                      border: `1px solid ${val === lvl ? CIA_LEVEL_COLOR[lvl] : '#1e3a5f'}`,
                      fontWeight: val === lvl ? 700 : 400,
                      '&:hover': { backgroundColor: `${CIA_LEVEL_COLOR[lvl]}22`, color: CIA_LEVEL_COLOR[lvl] },
                    }}
                  />
                ))}
              </Box>
            ))}
            {(filterC || filterI || filterA) && (
              <Chip label="Clear CIA" size="small" onClick={() => { setFilterC(''); setFilterI(''); setFilterA(''); }}
                sx={{ fontSize: 10, height: 20, backgroundColor: '#1e3a5f', color: '#7a8fa6', cursor: 'pointer', '&:hover': { color: '#ffffff' } }} />
            )}
          </Box>

          {/* Column Headers */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '90px 1fr 140px 130px 120px 100px 32px', p: '8px 14px', mb: 0.5 }}>
            {['Control', 'Title', 'Status', 'Frequency', 'Last Assessed', 'Flags', ''].map((h, i) => (
              <Typography key={i} sx={{ color: '#7a8fa6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</Typography>
            ))}
          </Box>

          {filtered.length === 0 ? (
            <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, p: 4, textAlign: 'center' }}>
              <Typography sx={{ color: '#4a5a70', fontSize: 14 }}>
                {controls.length === 0
                  ? 'No controls loaded yet. Use POST /api/ato/controls to add controls.'
                  : 'No controls match the current filters.'}
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {filtered.map(c => <ControlRow key={c.id} ctrl={c} systemId={systemId} onUpdated={load} />)}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
